import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { authApi } from "../utils/httpClient";
import type { ApiResponse } from "@industronics/fids-utils";
import type { Client } from "../api/clients";

type PlanLite = {
    _id: string;
    planKey: string;         // e.g. BASIC / PRO
    tierRank?: number;       // optional (recommended)
    name?: string;
};

function addOneYear(d: Date) {
    const x = new Date(d);
    x.setFullYear(x.getFullYear() + 1);
    return x;
}

function toIsoDateInput(d: Date) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

type Props = {
    open: boolean;
    client: Client | null;
    plans: PlanLite[];           // list of all plans (or upgradeable plans)
    onClose: () => void;
    onSuccess: () => void;
};

export default function UpgradePlanModal({
    open,
    client,
    plans,
    onClose,
    onSuccess,
}: Props) {
    const [effectiveAt, setEffectiveAt] = useState(toIsoDateInput(new Date()));
    const [endAt, setEndAt] = useState(toIsoDateInput(addOneYear(new Date())));

    const [newPlanId, setNewPlanId] = useState("");
    const [amount, setAmount] = useState("");
    const [currency, setCurrency] = useState("USD");
    const [invoiceNo, setInvoiceNo] = useState("");
    const [file, setFile] = useState<File | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const currentPlan = useMemo(() => {
        return (client as any)?.currentPlan;
    }, [client]);

    const upgradeOptions = useMemo(() => {
        // If you have tierRank, filter to higher rank; otherwise just remove current plan id.
        const currentPlanId = currentPlan?.planId ? String(currentPlan.planId) : "";
        const currentRank = currentPlan?.tierRank;

        return plans
            .filter((p) => String(p._id) !== currentPlanId)
            .filter((p) => {
                if (typeof currentRank === "number" && typeof p.tierRank === "number") {
                    return p.tierRank > currentRank; // upgrade only
                }
                return true;
            });
    }, [plans, currentPlan]);

    useEffect(() => {
        if (!open) return;

        setError(null);
        setSubmitting(false);
        setFile(null);
        setAmount("");
        setInvoiceNo("");
        setCurrency("USD");

        const now = new Date();
        setEffectiveAt(toIsoDateInput(now));
        setEndAt(toIsoDateInput(addOneYear(now)));

        // Preselect first upgrade option
        const first = upgradeOptions?.[0];
        setNewPlanId(first?._id || "");
    }, [open]); // intentionally only tied to open

    useEffect(() => {
        // Keep end date = +1 year from effective date (no proration)
        if (!effectiveAt) return;
        const d = new Date(`${effectiveAt}T00:00:00.000Z`);
        if (Number.isNaN(d.getTime())) return;
        setEndAt(toIsoDateInput(addOneYear(d)));
    }, [effectiveAt]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    if (!open || !client) return null;

    const missingCurrentPlan = !currentPlan?.planId;
    const noUpgradePlans = upgradeOptions.length === 0;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (missingCurrentPlan) {
            setError("Client does not have a current plan.");
            return;
        }
        if (!newPlanId) {
            setError("Please select a new plan to upgrade to.");
            return;
        }
        if (!file) {
            setError("Please upload invoice file");
            return;
        }

        // effectiveAt ISO (UTC midnight)
        const startIso = new Date(`${effectiveAt}T00:00:00.000Z`).toISOString();
        const endIso = new Date(`${endAt}T23:59:59.000Z`).toISOString();

        try {
            setSubmitting(true);

            // 1) Create billing transaction (multipart)
            const fd = new FormData();
            fd.append("file", file);

            fd.append("clientId", client._id);
            fd.append("planId", newPlanId);
            fd.append("periodStart", startIso);
            fd.append("periodEnd", endIso);

            if (amount.trim()) fd.append("amount", amount.trim());
            if (currency.trim()) fd.append("currency", currency.trim());
            if (invoiceNo.trim()) fd.append("invoiceNo", invoiceNo.trim());

            const txRes = await authApi.post<ApiResponse>("/billing-transactions/create", fd);
            if (!txRes.data.success) {
                throw new Error(txRes.data?.message || "Failed to create billing transaction");
            }

            const tx = txRes.data.data as any;
            const billingTransactionId = tx?._id;
            if (!billingTransactionId) throw new Error("Billing transaction id not returned");

            // 2) Upgrade plan (no proration)
            const upRes = await authApi.post<ApiResponse>(
                `/clients/${client._id}/subscription/plan/upgrade`,
                {
                    newPlanId,
                    billingTransactionId,
                    startAt: startIso,
                    endAt: endIso,
                }
            );

            if (!upRes.data.success) {
                throw new Error(upRes.data?.message || "Failed to upgrade plan");
            }

            toast.success("Plan upgraded successfully");
            onSuccess();
            onClose();
        } catch (e: any) {
            const msg = e?.message || "Failed to upgrade plan";
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <form onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold">Upgrade plan</h2>
                        <p className="text-sm text-gray-500">
                            Client:{" "}
                            <span className="font-medium text-gray-800">
                                {(client as any)?.name || client.email}
                            </span>
                        </p>
                    </div>

                    <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-sm hover:bg-gray-50"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Close
                    </button>
                </div>

                {missingCurrentPlan ? (
                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        This client does not have a current plan to upgrade from.
                    </div>
                ) : noUpgradePlans ? (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        No higher plan available to upgrade to.
                    </div>
                ) : error ? (
                    <div className="mb-4 whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}

                {!missingCurrentPlan && !noUpgradePlans && (
                    <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">Current plan</label>
                                <div className="rounded-xl border bg-gray-50 p-4">
                                    <p className="text-sm font-semibold text-gray-900">
                                        {currentPlan?.planKey || "—"}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-600">
                                        Status: <span className="font-medium">{currentPlan?.status || "—"}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">Upgrade to</label>
                                <select
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={newPlanId}
                                    onChange={(e) => setNewPlanId(e.target.value)}
                                    disabled={submitting}
                                >
                                    {upgradeOptions.map((p) => (
                                        <option key={p._id} value={p._id}>
                                            {p.planKey || p.name}
                                            {typeof p.tierRank === "number" ? ` (Tier ${p.tierRank})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Effective date
                                </label>
                                <input
                                    type="date"
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={effectiveAt}
                                    onChange={(e) => setEffectiveAt(e.target.value)}
                                    disabled={submitting}
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    End date
                                </label>
                                <input
                                    type="date"
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={endAt}
                                    onChange={(e) => setEndAt(e.target.value)}
                                    disabled={submitting}
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Amount (optional)
                                </label>
                                <input
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="4800"
                                    disabled={submitting}
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Currency (optional)
                                </label>
                                <input
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    placeholder="USD"
                                    disabled={submitting}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Invoice No (optional)
                                </label>
                                <input
                                    className="w-full rounded-xl border px-3 py-2"
                                    value={invoiceNo}
                                    onChange={(e) => setInvoiceNo(e.target.value)}
                                    placeholder="INV-2026-0002"
                                    disabled={submitting}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Invoice file (required)
                                </label>
                                <input
                                    type="file"
                                    className="w-full rounded-xl border px-3 py-2"
                                    accept=".pdf,image/png,image/jpeg"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    disabled={submitting}
                                />
                                <p className="mt-1 text-xs text-gray-500">Allowed: PDF, PNG, JPG</p>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-xl border px-4 py-2 hover:bg-gray-50"
                                disabled={submitting}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="rounded-xl bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
                                disabled={submitting}
                            >
                                {submitting ? "Processing…" : "Upgrade plan"}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
}
